import React from "react";
import {
  FaCalendarAlt,
  FaChevronDown,
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaFilter,
  FaPlus,
  FaSearch,
  FaTrash,
} from "react-icons/fa";

import Spinner from "../../../components/Spinner";
import { EVENT_TYPES, SCOPES } from "../constants";

type IconComponent = React.ComponentType<{ className?: string }>;

const CalendarAltIcon = FaCalendarAlt as IconComponent;
const ChevronDownIcon = FaChevronDown as IconComponent;
const EditIcon = FaEdit as IconComponent;
const EyeIcon = FaEye as IconComponent;
const EyeSlashIcon = FaEyeSlash as IconComponent;
const PlusIcon = FaPlus as IconComponent;
const FilterIcon = FaFilter as IconComponent;
const SearchIcon = FaSearch as IconComponent;
const TrashIcon = FaTrash as IconComponent;

const OverviewTab = ({
  t,
  i18n,
  filters,
  setFilters,
  scopeLabels,
  yearClassrooms,
  entityId,
  classroomLabel,
  eventTypeLabels,
  eventsLoading,
  filteredEvents,
  canAdd,
  openCreateForm,
  eventId,
  eventScope,
  eventClassroomNames,
  classroomLookup,
  sortedSchoolYears,
  schoolYearId,
  selectedSchoolYear,
  getDatePart,
  formatDateOnly,
  formatEventTime,
  canEdit,
  openEditForm,
  togglePublished,
  selectedEventIds = [],
  setSelectedEventIds,
  setSelectedPublished,
  requestDeleteSelected,
  saving,
  canRemove,
  setDeleteTarget,
  expandedEventId,
  setExpandedEventId,
  schoolYearLabel,
}: any) => {
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const activeFilterCount = ["scope", "classroomUuid", "eventType", "published", "from", "to"]
    .filter((key) => Boolean(filters?.[key])).length;

  return (
  // One wrapper for the filters and the list, so the search bar stays in view while scrolling through the list.
  <div className="space-y-5">
    <div className="sticky top-3 z-30">
      <div className="rounded-2xl border border-slate-200 bg-white/95 shadow-md backdrop-blur dark:border-slate-800 dark:bg-slate-950/95">
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">
          <SearchIcon className="shrink-0 text-slate-400" />
          <input
            type="search"
            value={filters.query || ""}
            onChange={(event) =>
              setFilters((current: any) => ({
                ...current,
                query: event.target.value,
              }))
            }
            aria-label={t("schoolCalendar.filters.search", { defaultValue: "Search planning items" })}
            placeholder={t("schoolCalendar.filters.search", { defaultValue: "Search planning items" })}
            className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-slate-100"
          />
          <span className="shrink-0 text-xs font-semibold text-slate-500 dark:text-slate-400">
            {t("schoolCalendar.overview.resultCount", {
              count: filteredEvents.length,
              defaultValue: "{{count}} items",
            })}
          </span>
          <button
            type="button"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls="school-calendar-filters"
            className={`inline-flex shrink-0 items-center gap-2 rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
              filtersOpen || activeFilterCount > 0
                ? "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-200"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            }`}
          >
            <FilterIcon />
            {t("schoolCalendar.filters.button", { defaultValue: "Filters" })}
            {activeFilterCount > 0 && (
              <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-indigo-600 px-1.5 text-[11px] text-white">
                {activeFilterCount}
              </span>
            )}
            <ChevronDownIcon className={`h-3 w-3 transition ${filtersOpen ? "rotate-180" : ""}`} />
          </button>
        </div>
        {filtersOpen && (
          <div id="school-calendar-filters" className="border-t border-slate-100 px-4 pb-4 pt-3 dark:border-slate-800">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t("schoolCalendar.fields.scope", { defaultValue: "Scope" })}
              <select
                value={filters.scope}
                onChange={(event) =>
                  setFilters((current: any) => ({
                    ...current,
                    scope: event.target.value,
                  }))
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950"
              >
                <option value="">
                  {t("schoolCalendar.filters.allScopes", {
                    defaultValue: "All scopes",
                  })}
                </option>
                {SCOPES.map((scope) => (
                  <option key={scope} value={scope}>
                    {scopeLabels[scope]}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t("schoolCalendar.fields.classrooms", { defaultValue: "Classrooms" })}
              <select
                value={filters.classroomUuid}
                onChange={(event) =>
                  setFilters((current: any) => ({
                    ...current,
                    classroomUuid: event.target.value,
                  }))
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950"
              >
                <option value="">
                  {t("schoolCalendar.filters.allClassrooms", {
                    defaultValue: "All classrooms",
                  })}
                </option>
                {yearClassrooms.map((classroom: any) => {
                  const id = String(entityId(classroom));
                  return (
                    <option key={id} value={id}>
                      {classroomLabel(classroom)}
                    </option>
                  );
                })}
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t("schoolCalendar.fields.eventType", { defaultValue: "Type" })}
              <select
                value={filters.eventType}
                onChange={(event) =>
                  setFilters((current: any) => ({
                    ...current,
                    eventType: event.target.value,
                  }))
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950"
              >
                <option value="">
                  {t("schoolCalendar.filters.allTypes", {
                    defaultValue: "All types",
                  })}
                </option>
                {EVENT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {eventTypeLabels[type]}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t("schoolCalendar.fields.published", {
                defaultValue: "Published",
              })}
              <select
                value={filters.published}
                onChange={(event) =>
                  setFilters((current: any) => ({
                    ...current,
                    published: event.target.value,
                  }))
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950"
              >
                <option value="">
                  {t("schoolCalendar.filters.allPublication", {
                    defaultValue: "All statuses",
                  })}
                </option>
                <option value="true">{t("common.yes", { defaultValue: "Yes" })}</option>
                <option value="false">{t("common.no", { defaultValue: "No" })}</option>
              </select>
            </label>

            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t("schoolCalendar.filters.from", { defaultValue: "Date from" })}
              <input
                type="date"
                value={filters.from}
                onChange={(event) =>
                  setFilters((current: any) => ({
                    ...current,
                    from: event.target.value,
                  }))
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950"
              />
            </label>

            <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {t("schoolCalendar.filters.to", { defaultValue: "Date to" })}
              <input
                type="date"
                value={filters.to}
                onChange={(event) =>
                  setFilters((current: any) => ({
                    ...current,
                    to: event.target.value,
                  }))
                }
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950"
              />
            </label>
            </div>
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={() =>
                  setFilters((current: any) => ({
                    ...current,
                    scope: "",
                    classroomUuid: "",
                    eventType: "",
                    published: "",
                    from: "",
                    to: "",
                  }))
                }
                className="mt-3 text-xs font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
              >
                {t("schoolCalendar.filters.clear", { defaultValue: "Clear filters" })}
              </button>
            )}
          </div>
        )}
      </div>
    </div>

    <section className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950">
      {eventsLoading ? (
        <div className="px-6 py-12">
          <Spinner />
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center text-gray-600 dark:text-gray-300">
          <CalendarAltIcon className="text-3xl text-indigo-500" />
          <p className="font-medium">
            {filters.scope === "CLASSROOM" || filters.classroomUuid
              ? t("schoolCalendar.emptyClassroom", {
                  defaultValue: "There are no classroom activities yet.",
                })
              : t("schoolCalendar.empty", {
                  defaultValue: "There are no school planning items yet.",
                })}
          </p>

          {canAdd && (
            <button
              type="button"
              onClick={openCreateForm}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
            >
              <PlusIcon />
              {t("schoolCalendar.add", {
                defaultValue: "Add planning item",
              })}
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          {canEdit && selectedEventIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 border-b border-indigo-100 bg-indigo-50/70 px-4 py-2 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/30">
              <span className="font-semibold text-indigo-900 dark:text-indigo-100">
                {t("schoolCalendar.selectedCount", { count: selectedEventIds.length, defaultValue: "{{count}} selected" })}
              </span>
              <button
                type="button"
                onClick={() => setSelectedPublished(true)}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <EyeIcon />
                {t("schoolCalendar.publishSelected", { defaultValue: "Publish selection" })}
              </button>
              <button
                type="button"
                onClick={() => setSelectedPublished(false)}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-indigo-800 dark:bg-slate-900 dark:text-indigo-200"
              >
                <EyeSlashIcon />
                {t("schoolCalendar.unpublishSelected", { defaultValue: "Unpublish selection" })}
              </button>
              {canRemove && (
                <button
                  type="button"
                  onClick={requestDeleteSelected}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-500/40 dark:bg-slate-900 dark:text-red-200"
                >
                  <TrashIcon />
                  {t("schoolCalendar.deleteSelected", { defaultValue: "Delete selection" })}
                </button>
              )}
              <button
                type="button"
                onClick={() => setSelectedEventIds([])}
                className="text-xs font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
              >
                {t("schoolCalendar.clearSelection", { defaultValue: "Clear selection" })}
              </button>
            </div>
          )}
          <table className="min-w-[960px] table-fixed divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-indigo-600 text-left text-[11px] font-semibold uppercase tracking-wide text-white dark:bg-indigo-700">
              <tr>
                {canEdit && (
                  <th className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={t("schoolCalendar.selectAll", { defaultValue: "Select all items" })}
                      checked={filteredEvents.length > 0 && filteredEvents.every((event: any) => selectedEventIds.includes(String(eventId(event))))}
                      onChange={(e) =>
                        setSelectedEventIds(e.target.checked ? filteredEvents.map((event: any) => String(eventId(event))) : [])
                      }
                      className="h-4 w-4 rounded border-white/60 accent-white"
                    />
                  </th>
                )}
                <th className="w-[30%] px-3 py-2">
                  {t("schoolCalendar.fields.title", { defaultValue: "Title" })}
                </th>
                <th className="w-32 px-3 py-2">
                  {t("schoolCalendar.fields.scope", { defaultValue: "Scope" })}
                </th>
                <th className="w-28 px-3 py-2">
                  {t("schoolCalendar.fields.eventType", { defaultValue: "Type" })}
                </th>
                <th className="w-32 px-3 py-2">
                  {t("schoolCalendar.fields.date", { defaultValue: "Date" })}
                </th>
                <th className="w-28 px-3 py-2">
                  {t("schoolCalendar.fields.time", { defaultValue: "Time" })}
                </th>
                <th className="w-28 px-3 py-2">
                  {t("schoolCalendar.fields.published", {
                    defaultValue: "Published",
                  })}
                </th>
                <th className="w-32 px-3 py-2 text-right">
                  {t("common.actions.name", { defaultValue: "Actions" })}
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredEvents.map((event: any) => {
                const id = String(eventId(event) || `${event?.title}-${event?.startDate}`);
                const published = Boolean(event?.published);
                const scope = eventScope(event);
                const classroomNames = eventClassroomNames(event, classroomLookup);
                const eventSchoolYear =
                  sortedSchoolYears.find(
                    (year: any) => String(entityId(year)) === String(schoolYearId(event)),
                  ) || selectedSchoolYear;
                const sameDay = getDatePart(event?.startDate) === getDatePart(event?.endDate);
                const expanded = expandedEventId === id;
                const selectionId = String(eventId(event));

                return (
                  <React.Fragment key={id}>
                    <tr
                      className={`h-10 align-middle transition ${
                        expanded
                          ? "border-l-4 border-indigo-500 bg-indigo-50/70 shadow-inner dark:bg-indigo-950/30"
                          : "border-l-4 border-transparent hover:bg-gray-50 dark:hover:bg-gray-900/40"
                      }`}
                    >
                      {canEdit && (
                        <td className="px-3 py-1.5">
                          <input
                            type="checkbox"
                            aria-label={t("schoolCalendar.selectItem", { title: event?.title || "-", defaultValue: "Select {{title}}" })}
                            checked={selectedEventIds.includes(selectionId)}
                            onChange={(e) =>
                              setSelectedEventIds((current: string[]) =>
                                e.target.checked ? [...current, selectionId] : current.filter((value) => value !== selectionId),
                              )
                            }
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                          />
                        </td>
                      )}
                      <td className="min-w-0 px-3 py-1.5 font-medium text-gray-900 dark:text-gray-100">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedEventId((current: string | null) =>
                              current === id ? null : id,
                            )
                          }
                          className="flex w-full min-w-0 items-center gap-2 text-left hover:text-indigo-700 dark:hover:text-indigo-300"
                          title={event?.title || "-"}
                          aria-expanded={expanded}
                        >
                          <ChevronDownIcon
                            className={`h-3 w-3 shrink-0 text-gray-400 transition ${
                              expanded ? "rotate-180" : ""
                            }`}
                          />
                          <span className="truncate">{event?.title || "-"}</span>
                        </button>
                      </td>

                      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200">
                        <span
                          className={`inline-flex max-w-full rounded-full px-2.5 py-1 text-xs font-semibold ${
                            scope === "CLASSROOM"
                              ? "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-100"
                              : "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-100"
                          }`}
                        >
                          <span className="truncate">{scopeLabels[scope] || scope}</span>
                        </span>
                      </td>

                      <td
                        className="truncate px-3 py-1.5 text-gray-700 dark:text-gray-200"
                        title={String(eventTypeLabels[event?.eventType] || event?.eventType || "-")}
                      >
                        {eventTypeLabels[event?.eventType] || event?.eventType || "-"}
                      </td>

                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-700 dark:text-gray-200">
                        {sameDay
                          ? formatDateOnly(event?.startDate, i18n.language)
                          : `${formatDateOnly(event?.startDate, i18n.language)} - ${formatDateOnly(
                              event?.endDate,
                              i18n.language,
                            )}`}
                      </td>

                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-700 dark:text-gray-200">
                        {formatEventTime(event, t)}
                      </td>

                      <td className="px-3 py-1.5">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            published
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100"
                              : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                          }`}
                        >
                          {published
                            ? t("common.yes", { defaultValue: "Yes" })
                            : t("common.no", { defaultValue: "No" })}
                        </span>
                      </td>

                      <td className="px-3 py-1.5">
                        <div className="flex justify-end gap-1.5">
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => openEditForm(event)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-100 dark:hover:bg-gray-700"
                              title={t("common.actions.edit", { defaultValue: "Bewerken" })}
                              aria-label={t("common.actions.edit", { defaultValue: "Bewerken" })}
                            >
                              <EditIcon />
                            </button>
                          )}

                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => togglePublished(event)}
                              disabled={saving}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-indigo-200 text-indigo-700 hover:bg-indigo-50 disabled:opacity-60 dark:border-indigo-500/40 dark:text-indigo-200 dark:hover:bg-indigo-900/30"
                              title={
                                published
                                  ? t("schoolCalendar.unpublish", { defaultValue: "Depubliceren" })
                                  : t("schoolCalendar.publish", { defaultValue: "Publiceren" })
                              }
                              aria-label={
                                published
                                  ? t("schoolCalendar.unpublish", { defaultValue: "Depubliceren" })
                                  : t("schoolCalendar.publish", { defaultValue: "Publiceren" })
                              }
                            >
                              {published ? <EyeSlashIcon /> : <EyeIcon />}
                            </button>
                          )}

                          {canRemove && (
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(event)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 text-red-700 hover:bg-red-50 dark:border-red-500/40 dark:text-red-200 dark:hover:bg-red-900/30"
                              title={t("common.actions.delete", { defaultValue: "Verwijderen" })}
                              aria-label={t("common.actions.delete", { defaultValue: "Verwijderen" })}
                            >
                              <TrashIcon />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {expanded && (
                      <tr className="border-l-4 border-indigo-500 bg-indigo-50/70 dark:bg-indigo-950/30">
                        <td colSpan={canEdit ? 8 : 7} className="px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                            <div>
                              <div className="text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                                {t("schoolCalendar.fields.schoolYear", { defaultValue: "Schooljaar" })}
                              </div>
                              <div>{schoolYearLabel(eventSchoolYear)}</div>
                            </div>

                            <div>
                              <div className="text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                                {t("schoolCalendar.fields.classrooms", { defaultValue: "Classrooms" })}
                              </div>
                              <div>
                                {scope === "CLASSROOM" && classroomNames.length > 0
                                  ? classroomNames.join(", ")
                                  : "-"}
                              </div>
                            </div>

                            <div>
                              <div className="text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                                {t("schoolCalendar.fields.location", { defaultValue: "Locatie" })}
                              </div>
                              <div>{event?.location || "-"}</div>
                            </div>

                            <div className="md:col-span-2 xl:col-span-1">
                              <div className="text-xs font-bold uppercase text-gray-600 dark:text-gray-300">
                                {t("schoolCalendar.fields.description", { defaultValue: "Omschrijving" })}
                              </div>
                              <div className="whitespace-pre-wrap">{event?.description || "-"}</div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  </div>
);
};

export default OverviewTab;
